import { createRouter, createWebHistory, type RouteLocationNormalized, type RouteRecordRaw } from 'vue-router'
import { useAuthStore, authCallbackPath, hasAuthCallbackQuery } from '@/stores/useAuthStore'
import { isAuth0Configured } from '@/config/clientPortalConfig'
import { applyPageTitle } from '@/utils/pageTitle'

var loggedInRouteLoaders = [
  () => import('@/layouts/LoggedInMainLayout.vue'),
  () => import('@/views/MyDashboardView.vue'),
  () => import('@/views/MyInfoView.vue'),
  () => import('@/views/PoliciesView.vue'),
  () => import('@/views/PolicyDetailsView.vue'),
  () => import('@/views/EditTripDatesView.vue'),
  () => import('@/views/EditCreditCardView.vue'),
  () => import('@/views/DocumentsView.vue'),
  () => import('@/views/QuotesView.vue'),
  () => import('@/views/CallbackFormView.vue'),
  () => import('@/components/portal/PortalBottomImage.vue'),
  () => import('@/components/portal/PortalHero.vue'),
]

function routeRequiresAuth(to: RouteLocationNormalized) {
  return to.matched.some((record) => record.meta.requiresAuth)
}

function loggedInChildRoute(
  path: string,
  name: string,
  view: () => Promise<{ default: unknown }>,
  meta: Record<string, unknown> = {},
  props = false,
): RouteRecordRaw {
  var components: Record<string, () => Promise<{ default: unknown }>> = { default: view }
  if (meta.bottomImageVariant) {
    components['bottom-image'] = () => import('@/components/portal/PortalBottomImage.vue')
  }
  if (meta.heroMode) {
    components['top-content'] = () => import('@/components/portal/PortalHero.vue')
  }
  return { path, name, components, meta, props: props || undefined }
}

var loggedInChildren: RouteRecordRaw[] = [
  loggedInChildRoute('my-dashboard', 'my-dashboard', loggedInRouteLoaders[1], {
    bottomImageVariant: 'auto',
    heroMode: 'welcome',
    titleKey: 'MyTrips',
  }),
  loggedInChildRoute('my-dashboard/my-info', 'my-info', loggedInRouteLoaders[2], {
    bottomImageVariant: 'my-info',
    titleKey: 'MyInfo',
  }),
  loggedInChildRoute('my-dashboard/policies', 'policies', loggedInRouteLoaders[3], {
    bottomImageVariant: 'pier',
    titleKey: 'Policies',
  }),
  loggedInChildRoute('my-dashboard/policies/:policyId', 'policy-details', loggedInRouteLoaders[4], {
    bottomImageVariant: 'luggage',
    titleKey: 'Policies',
  }, true),
  loggedInChildRoute('my-dashboard/policies/:policyId/edit-trip-dates', 'edit-trip-dates', loggedInRouteLoaders[5], {
    bottomImageVariant: 'edit-trip-dates',
    titleKey: 'EditTripDatesTitle',
  }, true),
  loggedInChildRoute('my-dashboard/update-payment-information', 'edit-credit-card', loggedInRouteLoaders[6], {
    bottomImageVariant: 'edit-credit-card',
    titleKey: 'EditCreditCardTitle',
  }),
  loggedInChildRoute('my-dashboard/documents', 'documents', loggedInRouteLoaders[7], {
    titleKey: 'Documents',
  }),
  loggedInChildRoute('my-dashboard/quotes', 'quotes', loggedInRouteLoaders[8], {
    titleKey: 'Quotes',
  }),
  loggedInChildRoute('callback-form', 'callback-form', loggedInRouteLoaders[9], {
    bottomImageVariant: 'auto',
    heroMode: 'title',
    heroTitleKey: 'RequestCallback',
    titleKey: 'RequestCallback',
  }),
]

export var router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior() {
    return { top: 0, left: 0 }
  },
  routes: [
    {
      path: '/',
      component: loggedInRouteLoaders[0],
      meta: { requiresAuth: true },
      redirect: { name: 'my-dashboard' },
      children: loggedInChildren,
    },
    { path: '/contact-us', name: 'contact-us', component: () => import('@/views/ContactUsView.vue'), meta: { titleKey: 'Contact US' } },
    { path: authCallbackPath, name: 'auth-callback', component: () => import('@/views/AuthCallbackView.vue'), meta: { allowAnonymous: true } },
    { path: '/login', name: 'login', component: () => import('@/views/LoginView.vue'), meta: { allowAnonymous: true, titleKey: 'LogIn' } },
    // Legacy Blazor login path used by Auth0 password-change ResultUrl in production App Config.
    {
      path: '/Account/Login',
      redirect: (to) => ({ path: '/login', query: to.query }),
    },
    { path: '/confirmation', name: 'confirmation', component: () => import('@/views/ConfirmationView.vue'), meta: { allowAnonymous: true, titleKey: 'Confirmation' } },
    { path: '/set-culture/:culture', name: 'set-culture', component: () => import('@/views/SetCultureView.vue'), meta: { allowAnonymous: true } },
    { path: '/buy-quote/:purchaseLinkId/:clientId', name: 'direct-buy-quote', component: () => import('@/views/DirectBuyQuoteView.vue'), props: true, meta: { allowAnonymous: true, titleKey: 'Payment' } },
    { path: '/sign-up/:email/:code', name: 'sign-up', component: () => import('@/views/SignUpView.vue'), props: true, meta: { allowAnonymous: true, titleKey: 'Sign-up' } },
    { path: '/sign-up-redirect/en/:email/:code', name: 'sign-up-en', component: () => import('@/views/SignUpView.vue'), props: true, meta: { allowAnonymous: true, titleKey: 'Sign-up' } },
    { path: '/sign-up-redirect/fr/:email/:code', name: 'sign-up-fr-loading', component: () => import('@/views/SignUpFrLoadingView.vue'), props: true, meta: { allowAnonymous: true, titleKey: 'Sign-up' } },
  ],
})

export function preloadLoggedInRoutes() {
  loggedInRouteLoaders.forEach((load) => {
    load()
  })
}

router.afterEach((to) => {
  var titleKey = to.meta.titleKey as string | undefined
  applyPageTitle(titleKey)
})

router.beforeEach(async (to) => {
  var authStore = useAuthStore()
  if (to.path === authCallbackPath) {
    if (hasAuthCallbackQuery()) {
      return await authStore.handleAuthCallback()
    }
    return '/my-dashboard'
  }
  if (!authStore.initialized) {
    await authStore.init()
  }
  if (routeRequiresAuth(to) && !authStore.isAuthenticated) {
    if (!isAuth0Configured()) {
      return { name: 'contact-us' }
    }
    await authStore.login(to.fullPath)
    return false
  }
  if (to.query.pCode && typeof to.query.pCode === 'string') {
    var { usePartnerStore } = await import('@/stores/usePartnerStore')
    await usePartnerStore().loadFromPromoCode(to.query.pCode)
  }
})
